import { Box, Divider, IconButton, Text, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { ChevronLeftLarge, ExternalLink } from '@wix/wix-ui-icons-common';
import React, { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Page, useCurrentPage } from '../../../common/hooks/useCurrentPage';
import { selectArticleHeadingVisible } from '../../../common/selectors/helpCenter';
import { homepageService } from '../../../common/services/homepage/homepageService';
import { TLocalesKeys } from '../../../types';
import { biHelpCenter } from '../../../util/bi';
import s from './styles.scss';

export type SubtitleProps = {
  text?: string;
  href?: string;
  articleId?: string;
  sendBackBi?: () => void;
  hideBackButton?: boolean;
};

export const Subtitle: FC<SubtitleProps> = ({
  text,
  href,
  sendBackBi,
  articleId,
  hideBackButton,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentPage = useCurrentPage();
  const articleHeadingVisible = useSelector(selectArticleHeadingVisible);
  const goBack = useCallback(() => {
    sendBackBi && sendBackBi();
    navigate(-1);
  }, [navigate, sendBackBi]);
  const logOpenArticleInHC = useCallback(
    () => biHelpCenter.logOpenArticleInHC(articleId),
    [articleId],
  );

  const shouldShowArticleHeading: boolean =
    currentPage === Page.Article && articleHeadingVisible;

  if (homepageService.homepageEnabled) {
    return shouldShowArticleHeading ? (
      <>
        <Box
          padding="6px 18px"
          justifyContent="space-between"
          verticalAlign="middle"
        >
          <Text size="medium" style={{ maxWidth: '72%' }} ellipsis>
            {text}
          </Text>
          <IconButton
            className={s.subtitleIcon}
            as="a"
            href={href}
            target="_blank"
            skin="light"
            dataHook="help-center-subtitle__link"
            onClick={logOpenArticleInHC}
          >
            <ExternalLink />
          </IconButton>
        </Box>
        <Divider />
      </>
    ) : null;
  }

  const shouldRenderDivider = text && !hideBackButton;

  return (
    <>
      <Box
        height="48px"
        padding={hideBackButton ? '6px 18px' : '6px'}
        verticalAlign="middle"
        gap="13px"
        flexShrink={0}
      >
        {!hideBackButton && (
          <TextButton
            prefixIcon={<ChevronLeftLarge />}
            onClick={goBack}
            dataHook="help-center-subtitle__back-button"
          >
            {t<TLocalesKeys>('back_widget')}
          </TextButton>
        )}
        {shouldRenderDivider && <Divider direction="vertical" />}
        {text && (
          <Text
            dataHook="help-center-subtitle__text"
            weight="normal"
            ellipsis
            tooltipProps={{
              placement: 'bottom',
              maxWidth: '72%',
            }}
          >
            {text}
          </Text>
        )}
        {href && (
          <IconButton
            className={s.subtitleIcon}
            as="a"
            href={href}
            target="_blank"
            skin="light"
            dataHook="help-center-subtitle__link"
            onClick={logOpenArticleInHC}
          >
            <ExternalLink />
          </IconButton>
        )}
      </Box>
      <Divider />
    </>
  );
};
